/**
 * Reads a pasted platform token well enough to decide whether it is worth
 * sending, and to say why not when it is not.
 *
 * Not `src/utils/tokens.ts`: `extractDataFromToken` throws on malformed input,
 * which is the normal case for a paste box, and it `atob`s straight to a string,
 * which mangles a name like "Söderman" — the payload is UTF-8 and atob hands
 * back one byte per character.
 *
 * Everything here decodes without verifying. That is correct for reading our own
 * credential to find out which team it names, and would be badly wrong anywhere
 * a decision depends on the answer. The backend verifies; nothing here is
 * trusted by anything.
 */

/** A token this close to expiry will not survive the session; treat it as gone. */
const EXPIRY_MARGIN_SECONDS = 30

export interface TokenInfo {
  /** Tagged team ids, e.g. `team:01KRX…`, which is the form X-Team-Id wants. */
  teams: string[]
  /** Seconds since the epoch, if the token says. */
  expiresAt?: number
  displayName?: string
}

const decodePayload = (token: string): Record<string, unknown> | undefined => {
  const part = token.split('.')[1]
  if (!part) return undefined
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')
    const bytes = Uint8Array.from(atob(base64), ch => ch.charCodeAt(0))
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return typeof parsed === 'object' && parsed !== null ? parsed as Record<string, unknown> : undefined
  }
  catch {
    return undefined
  }
}

/** What a token says about itself, for choosing a team and for the UI. */
export const inspect = (token: string): TokenInfo | undefined => {
  const payload = decodePayload(token.trim())
  if (!payload) return undefined

  // Core's claim is an object keyed by tagged team id; the values are
  // privileges, which we have no use for here.
  const teamsClaim = payload.teams
  const teams
    = typeof teamsClaim === 'object' && teamsClaim !== null && !Array.isArray(teamsClaim)
      ? Object.keys(teamsClaim).filter(key => key.startsWith('team:'))
      : []

  const user = payload.user
  const first
    = typeof user === 'object' && user !== null ? (user as { first_name?: unknown }).first_name : undefined

  return {
    teams,
    ...(typeof payload.exp === 'number' ? { expiresAt: payload.exp } : {}),
    ...(typeof first === 'string' && first.trim() ? { displayName: first.trim() } : {}),
  }
}

export const hasExpired = (info: TokenInfo, now = Date.now()): boolean => {
  if (info.expiresAt === undefined) return false
  return info.expiresAt - EXPIRY_MARGIN_SECONDS <= now / 1000
}

/**
 * Why a token cannot be used, in words worth showing someone. Returns undefined
 * when it can.
 */
export const rejectionReason = (token: string): string | undefined => {
  const info = inspect(token)
  if (!info) return 'That does not look like a JWT — three dot-separated parts, and the middle one is JSON.'
  if (hasExpired(info)) return 'That token has expired. Get a fresh one and paste it again.'
  if (info.teams.length === 0) return 'That token grants no teams, so there is nothing for the chat to act in.'
  return undefined
}
